/**
 * The approval queue (P1-TR3, BR-07, P1-BR1).
 *
 * This screen is where maker-checker becomes a habit rather than a rule in a
 * document, so it is built to make approving quick and approving carelessly
 * hard. One tap approves; rejecting asks for a reason, because BR-08 says an
 * entry that disappears without an explanation is indistinguishable, months
 * later, from one that was never recorded.
 *
 * A partner's own entries show "waiting for another partner" instead of an
 * approve button (P1-BR1). That is a courtesy, not a control — the server
 * refuses self-approval regardless, and the button being hidden is not what
 * makes it safe (SEC-3).
 */

import { useState } from 'react';

import { useAuth } from '../auth/AuthContext.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { formatMoney } from '../../lib/currency.js';
import { formatDate } from '../../lib/date.js';
import {
  useApproveTransaction,
  useCategories,
  useRejectTransaction,
  useTransactions,
  type Transaction,
} from './api.js';
import { ReceiptViewer } from './ReceiptViewer.js';

export function ApprovalQueue(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();

  const { data, isLoading, isError, refetch } = useTransactions({ status: 'pending' });
  const { data: categories = [] } = useCategories();

  const approve = useApproveTransaction();
  const reject = useRejectTransaction();

  /** Which entry is mid-rejection, and the reason typed so far. */
  const [rejecting, setRejecting] = useState<{ id: number; reason: string } | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  /** Which row is being acted on, so only its buttons show a busy state. */
  const [busyId, setBusyId] = useState<number | null>(null);

  const categoryName = (id: number | null): string => {
    if (id === null) return t('finance.uncategorised');
    for (const parent of categories) {
      if (parent.id === id) return parent.name;
      const child = parent.children?.find((c) => c.id === id);
      if (child) return child.name;
    }
    return t('finance.uncategorised');
  };

  async function handleApprove(txn: Transaction): Promise<void> {
    setErrorKey(null);
    setBusyId(txn.id);
    try {
      await approve.mutateAsync(txn.id);
    } catch (error) {
      // ALREADY_PROCESSED is the common one: another partner approved it while
      // this screen was open. The message says so rather than looking broken.
      setErrorKey(errorMessageKey(error));
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(): Promise<void> {
    if (!rejecting || rejecting.reason.trim().length < 3) return;

    setErrorKey(null);
    setBusyId(rejecting.id);
    try {
      await reject.mutateAsync({ id: rejecting.id, reason: rejecting.reason.trim() });
      setRejecting(null);
    } catch (error) {
      setErrorKey(errorMessageKey(error));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">
        {t('finance.approvals.title')}
        {data && data.total > 0 && (
          <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-sm text-amber-900">{data.total}</span>
        )}
      </h1>

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {errorKey !== null && (
        <p role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          {t(errorKey)}
        </p>
      )}

      {data?.items.length === 0 && <p className="py-8 text-center text-gray-500">{t('finance.approvals.empty')}</p>}

      <ul className="space-y-3">
        {data?.items.map((txn) => {
          const isOwn = txn.createdById === user?.id;
          const isBusy = busyId === txn.id;

          return (
            <li key={txn.id} className="rounded-xl bg-white p-4 shadow-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate font-medium text-gray-800">{categoryName(txn.categoryId)}</span>
                <span className="shrink-0 text-lg font-semibold tabular-nums text-gray-900">
                  {formatMoney(txn.amount)}
                </span>
              </div>

              <p className="mt-1 text-xs text-gray-500">
                {formatDate(txn.txnDate)}
                {txn.isBackdated && ` · ${t('finance.backdated')}`}
              </p>
              {txn.note !== null && <p className="mt-1 text-sm text-gray-600">{txn.note}</p>}

              {/* BR-06 — the approver checks the figure against the paper, so the
                  paper has to be on this screen, not a label saying it exists. */}
              {txn.receiptUrl !== null && <ReceiptViewer uploadId={txn.receiptUrl} />}

              {isOwn ? (
                // P1-BR1 — the maker never checks their own work.
                <p className="mt-3 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600">
                  {t('finance.approvals.yourEntry')}
                </p>
              ) : rejecting?.id === txn.id ? (
                <div className="mt-3">
                  <label className="mb-1 block text-sm text-gray-700" htmlFor={`reason-${String(txn.id)}`}>
                    {t('finance.approvals.reason')}
                  </label>
                  <input
                    id={`reason-${String(txn.id)}`}
                    value={rejecting.reason}
                    onChange={(event) => setRejecting({ id: txn.id, reason: event.target.value })}
                    className="mb-2 min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => void handleReject()}
                      disabled={rejecting.reason.trim().length < 3 || isBusy}
                      className="min-h-touch flex-1 rounded-lg bg-red-700 px-3 py-2 font-medium text-white disabled:opacity-50"
                    >
                      {t('finance.approvals.confirmReject')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRejecting(null)}
                      className="min-h-touch flex-1 rounded-lg bg-gray-100 px-3 py-2 text-gray-700"
                    >
                      {t('finance.approvals.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => void handleApprove(txn)}
                    disabled={isBusy}
                    className="min-h-touch flex-1 rounded-lg bg-green-700 px-3 py-2 font-medium text-white disabled:opacity-50"
                  >
                    {t(isBusy ? 'finance.approvals.approving' : 'finance.approvals.approve')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRejecting({ id: txn.id, reason: '' })}
                    className="min-h-touch rounded-lg bg-white px-4 py-2 text-red-700 shadow-sm"
                  >
                    {t('finance.approvals.reject')}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
